import React, { useEffect, useRef, useState } from 'react';
import { Card, CardBody, Button, Spinner, ListGroup, ListGroupItem, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import documentsApi from 'service/documentsApi';
import DocumentDownloadButton from './DocumentDownloadButton';
import DocumentPreview from './DocumentPreview';

const getFileNameFromKey = (d) => {
  const key = d?.s3Key || d?.s3key || d?.s3_key || '';
  if (typeof key === 'string' && key.includes('/')) {
    const last = key.lastIndexOf('/') + 1;
    return key.substring(last);
  }
  return d?.name || d?.fileName || d?.filename || String(d?.documentId || '');
};

// Búsqueda removida: solo listado por origen

// Filtros por texto eliminados

const prettySize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024; i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
};

const normalizeTags = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  try {
    // If backend returns comma-separated string
    return String(raw)
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  } catch (_) {
    return [];
  }
};

const DocumentSearchList = ({ originId, originType, pageSize = 20, refreshKey, showDeleteButton = true, onDeleted, onDocumentsLoaded }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const latestReq = useRef(0);
  const [confirmDelete, setConfirmDelete] = useState(null); // item a eliminar
  const [deleting, setDeleting] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null); // { name }

  const fetchPage = async (nextPage = 0) => {
    setLoading(true);
    setError(null);
    const reqId = Date.now();
    latestReq.current = reqId;
    try {
      // Nuevo endpoint por origen; ignoramos búsqueda por texto por ahora
      const { items: rows, pageInfo } = await documentsApi.listDocumentsByOrigin({ originType, originId, page: nextPage, size: pageSize, sortBy: 'id', sortDirection: 'desc' });
      if (latestReq.current !== reqId) return; // descartar respuesta antigua
      const newItems = nextPage === 0 ? rows : [...items, ...rows];
      setItems(newItems);
      setPage(pageInfo.page || nextPage);
      const total = pageInfo.totalElements || (nextPage + 1) * pageSize;
      setHasMore((nextPage + 1) * pageSize < total);
      
      // Llamar callback con los documentos cargados
      if (onDocumentsLoaded && rows.length > 0) {
        onDocumentsLoaded(rows.map(doc => ({
          id: doc.documentId,
          originType: originType,
          ...doc
        })));
      }
    } catch (e) {
      if (latestReq.current !== reqId) return;
      setError(e?.message || 'Error buscando documentos');
    } finally {
      if (latestReq.current === reqId) setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(0);
  }, [originId, originType, refreshKey]);

  return (
    <Card className="shadow-sm">
      <CardBody>
        <style>{`
          .doc-actions .doc-action-btn { width: 36px; height: 28px; display: inline-flex; align-items: center; justify-content: center; }
          .doc-item { border: 1px solid #dee2e6 !important; border-radius: 0.375rem !important; padding: 0.5rem !important; }
          .doc-item:not(:last-child) { margin-bottom: 0.5rem !important; }
          .doc-item .font-weight-bold { font-size: 0.85rem !important; }
          .doc-item .text-muted { font-size: 0.75rem !important; }
          .doc-item .mt-1 { margin-top: 0.25rem !important; }
          @media (max-width: 767.98px) {
            .doc-actions { width: 100% !important; }
            .doc-actions .btn, .doc-actions .document-download-button, .doc-actions .doc-action-btn { width: 100% !important; height: 36px !important; justify-content: center !important; margin-left: 0 !important; }
            .doc-tags { align-items: center !important; }
            .doc-tags small { margin-bottom: 4px; }
          }
          @media (min-width: 768px) and (max-width: 991.98px) {
            .doc-item { display: grid; grid-template-columns: 1fr; grid-row-gap: 8px; }
            .doc-actions { justify-content: flex-start !important; }
          }
        `}</style>
        {error && <div className="text-danger mb-2" style={{ fontSize: 13 }}>{error}</div>}
        <ListGroup>
          {items.map((d) => (
            <ListGroupItem key={d.documentId} className="d-flex flex-column flex-md-row justify-content-between align-items-md-center doc-item">
              <div className="mr-md-3 flex-grow-1" style={{ minWidth: 0 }}>
                <div className="font-weight-bold text-truncate" title={getFileNameFromKey(d)}>
                  {getFileNameFromKey(d)}
                </div>
                {d.description && (
                  <div className="mt-1" style={{ fontSize: 14, color: '#4a5568' }}>
                    <span>{String(d.description)}</span>
                  </div>
                )}
                {normalizeTags(d.tags).length > 0 && (
                  <div className="mt-1 d-flex align-items-center flex-wrap doc-tags">
                    <small className="text-muted d-flex align-items-center mr-2" style={{ opacity: 0.85 }}>
                      <i className="ni ni-tag mr-1" /> User Tags:
                    </small>
                    {normalizeTags(d.tags).map((t, idx) => (
                      <span key={`${t}-${idx}`} className="mb-1" style={{ color: '#6c7a86' }}>
                        {String(t)}{idx < normalizeTags(d.tags).length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {d.createdBy ? `By ${d.createdBy}` : ''} {d.createdAt ? `• ${new Date(d.createdAt).toLocaleString()}` : ''}
                </div>
              </div>
              <div className="mt-2 mt-md-0 d-flex flex-wrap flex-shrink-0 doc-actions">
                <div className="mr-md-2 mb-2 mb-md-0" style={{ display: 'inline-flex' }}>
                  <DocumentDownloadButton documentId={d.documentId} className="document-download-button doc-action-btn" label={<i className="ni ni-cloud-download-95" style={{ fontSize: '1rem' }} />} />
                </div>
                <Button color="secondary" size="sm" className="ml-0 ml-md-2 mt-2 mt-md-0 doc-action-btn" onClick={() => setPreviewId(d.documentId)} aria-label="Preview">
                  <i className="ni ni-album-2" style={{ fontSize: '1rem' }} />
                </Button>
                {showDeleteButton && (
                  <Button color="danger" size="sm" className="ml-0 ml-md-2 mt-2 mt-md-0 doc-action-btn" onClick={() => setConfirmDelete(d)} aria-label="Delete">
                    <i className="ni ni-fat-remove" style={{ fontSize: '1rem' }} />
                  </Button>
                )}
              </div>
            </ListGroupItem>
          ))}
        </ListGroup>
        {/* Modal de Preview */}
        {previewId && (
          <DocumentPreview documentId={previewId} isOpen={true} onClose={() => setPreviewId(null)} />
        )}
        {/* Modal de confirmación de borrado */}
        {confirmDelete && (
          <Modal isOpen={true} toggle={() => (deleting ? null : setConfirmDelete(null))}>
            <ModalHeader toggle={() => (deleting ? null : setConfirmDelete(null))}>Delete document</ModalHeader>
            <ModalBody>
              Are you sure you want to delete "{getFileNameFromKey(confirmDelete)}"?
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</Button>
              <Button color="danger" onClick={async () => {
                if (!confirmDelete) return;
                setDeleting(true);
                try {
                  await documentsApi.deleteDocument(confirmDelete.documentId);
                  setItems((prev) => prev.filter((x) => x.documentId !== confirmDelete.documentId));
                  const deletedName = getFileNameFromKey(confirmDelete);
                  
                  // Llamar callback con el documento eliminado
                  if (onDeleted) {
                    onDeleted({
                      id: confirmDelete.documentId,
                      originType: originType,
                      ...confirmDelete
                    });
                  }
                  
                  setConfirmDelete(null);
                  setSuccessInfo({ name: deletedName });
                } catch (e) {
                  // mostrar error en el mismo modal
                  alert(e?.message || 'Failed to delete document');
                } finally {
                  setDeleting(false);
                }
              }} disabled={deleting}>
                {deleting ? <Spinner size="sm" /> : 'Delete'}
              </Button>
            </ModalFooter>
          </Modal>
        )}
        {/* Modal de éxito */}
        {successInfo && (
          <Modal isOpen={true} toggle={() => setSuccessInfo(null)}>
            <ModalHeader toggle={() => setSuccessInfo(null)}>Document deleted</ModalHeader>
            <ModalBody>
              "{successInfo.name}" was deleted successfully.
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={() => setSuccessInfo(null)}>OK</Button>
            </ModalFooter>
          </Modal>
        )}
        {/* <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted" style={{ fontSize: 12 }}>Results: {items.length}</div>
          {hasMore && (
            <Button color="primary" size="sm" onClick={() => fetchPage(page + 1)} disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Load more'}
            </Button>
          )}
        </div> */}
      </CardBody>
    </Card>
  );
};

export default DocumentSearchList;


